import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddToCalendar from './AddToCalendar';

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const ProviderCalendarView = ({ appointments, services, staff, staffSchedules, onReschedule, onCancel }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayView, setShowDayView] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });
  
  // Debug: Log appointments
  useEffect(() => {
    console.log('Calendar received appointments:', appointments.length);
    console.log('Sample appointments:', appointments.slice(0, 3));
  }, [appointments]);

  // Get month dates
  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const dates = [];
    
    // Add days from previous month to fill first week
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(firstDay);
      day.setDate(day.getDate() - (i + 1));
      dates.push({ date: day, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add days from next month to fill last week
    const remaining = 7 - (dates.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const day = new Date(lastDay);
        day.setDate(lastDay.getDate() + i);
        dates.push({ date: day, isCurrentMonth: false });
      }
    }
    
    return dates;
  };

  // Generate time slots for a day (30-minute intervals from 8 AM to 8 PM)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) {
      for (let minute of [0, 30]) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    // Use local date format to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const filtered = appointments.filter(apt => {
      const aptDateStr = apt.appointment_date;
      const statusMatch = apt.status !== 'cancelled';
      const dateMatch = aptDateStr === dateStr;
      
      return dateMatch && statusMatch;
    });
    
    return filtered;
  };

  // Count available slots for a date
  const getAvailableSlotsCount = (date) => {
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    // Use local date format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    let availableCount = 0;

    // Check each time slot
    timeSlots.forEach(time => {
      // Check if any staff member is available at this time
      for (const [staffId, schedule] of Object.entries(staffSchedules)) {
        const daySchedule = schedule[dayName] || [];
        
        for (const slot of daySchedule) {
          if (slot.type === 'work') {
            const slotStart = slot.start;
            const slotEnd = slot.end;
            
            if (time >= slotStart && time < slotEnd) {
              // Check if this staff member is available (not blocked by appointment duration)
              const [slotHour, slotMin] = time.split(':').map(Number);
              const slotTime = slotHour * 60 + slotMin;
              
              const isBlocked = appointments.some(apt => {
                if (apt.appointment_date !== dateStr || apt.staff_id !== staffId || apt.status === 'cancelled') {
                  return false;
                }
                
                // Parse appointment start time
                const [aptHour, aptMin] = apt.appointment_time.split(':').map(Number);
                const aptStartTime = aptHour * 60 + aptMin;
                const duration = apt.duration || 30;
                const aptEndTime = aptStartTime + duration;
                
                // Check if slot falls within appointment duration
                return slotTime >= aptStartTime && slotTime < aptEndTime;
              });
              
              if (!isBlocked) {
                availableCount++;
                return; // Count this slot once
              }
            }
          }
        }
      }
    });
    
    return availableCount;
  };

  // Navigate calendar
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  // Handle day click
  const handleDayClick = (date) => {
    setSelectedDate(date);
    setShowDayView(true);
  };

  // Handle reschedule
  const handleRescheduleClick = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.appointment_date,
      time: appointment.appointment_time
    });
    setShowRescheduleModal(true);
    setShowDayView(false);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleData.date || !rescheduleData.time) {
      alert(t('calendarView.selectDateTime', 'Please select both date and time'));
      return;
    }
    
    await onReschedule(selectedAppointment.id, rescheduleData.date, rescheduleData.time);
    setShowRescheduleModal(false);
    setSelectedAppointment(null);
  };

  const monthDates = getMonthDates(currentDate);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {t(`months.${MONTH_KEYS[currentDate.getMonth()]}`)} <span className="numeric-ltr">{currentDate.getFullYear()}</span>
        </h2>
        
        <div className="flex gap-2">
          <button
            onClick={navigatePrevious}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={navigateToday}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
            data-testid="calendar-today-btn"
          >
            {t('calendarView.today')}
          </button>
          <button
            onClick={navigateNext}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
          <span>{t('calendarView.available')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
          <span>{t('calendarView.noSlots')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-500 rounded"></div>
          <span>{t('calendarView.selected')}</span>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {DAY_KEYS.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {t(`days.${day}`)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {monthDates.map((item, idx) => {
          const { date, isCurrentMonth } = item;
          const dayAppointments = getAppointmentsForDate(date);
          const availableSlots = getAvailableSlotsCount(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const isPast = date < new Date().setHours(0, 0, 0, 0);
          
          return (
            <button
              key={idx}
              onClick={() => handleDayClick(date)}
              disabled={!isCurrentMonth || isPast}
              className={`
                relative p-3 min-h-[100px] rounded-lg border-2 transition-all
                ${!isCurrentMonth ? 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-50' : 
                  isPast ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60' :
                  availableSlots > 0 ? 'bg-green-50 border-green-200 hover:border-green-400 hover:shadow-md cursor-pointer' : 
                  'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'}
                ${isToday ? 'border-blue-500 ring-2 ring-blue-200' : ''}
              `}
            >
              <div className="text-start">
                <div className={`text-lg sm:text-2xl font-bold mb-2 ${
                  !isCurrentMonth ? 'text-gray-400' : 
                  isToday ? 'text-blue-600' : 
                  isPast ? 'text-gray-400' :
                  'text-gray-900'
                }`}>
                  {date.getDate()}
                </div>
                
                {isCurrentMonth && !isPast && (
                  <>
                    {availableSlots > 0 && (
                      <div className="text-xs font-semibold text-green-700 mb-1">
                        {t('calendarView.slots', { count: availableSlots })}
                      </div>
                    )}
                    
                    {availableSlots === 0 && dayAppointments.length === 0 && (
                      <div className="text-xs text-gray-500">
                        {t('calendarView.noSlots')}
                      </div>
                    )}
                    
                    {dayAppointments.length > 0 && (
                      <div className="text-xs font-semibold text-blue-700">
                        {t('calendarView.bookings', { count: dayAppointments.length })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Day View Modal */}
      {showDayView && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {getAppointmentsForDate(selectedDate).length} appointments scheduled
                </p>
              </div>
              <button
                onClick={() => setShowDayView(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Appointments List */}
            <div className="p-6">
              {getAppointmentsForDate(selectedDate).length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg">No appointments scheduled</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getAppointmentsForDate(selectedDate)
                    .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time))
                    .map((apt) => {
                      const staffMember = staff.find(s => s.id === apt.staff_id);
                      return (
                        <div
                          key={apt.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-semibold text-sm">
                                  {apt.appointment_time}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  apt.status === 'confirmed' || apt.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                                  apt.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {apt.status}
                                </span>
                              </div>
                              
                              <h4 className="font-semibold text-lg text-gray-900 mb-1">
                                {apt.patient_name}
                              </h4>
                              
                              <div className="space-y-1 text-sm text-gray-600">
                                <p className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <span className="font-medium">{apt.service_name}</span>
                                </p>
                                
                                {staffMember && (
                                  <p className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Staff: {staffMember.name}
                                  </p>
                                )}
                                
                                <p className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {apt.consultation_fee} KD - {apt.payment_method || 'cash'}
                                </p>
                                
                                {apt.symptoms && (
                                  <p className="text-gray-500 mt-2 italic">
                                    "{apt.symptoms}"
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {(apt.status === 'confirmed' || apt.status === 'scheduled') && (
                              <div className="flex flex-col gap-2 ml-4">
                                <button
                                  onClick={() => handleRescheduleClick(apt)}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                                >
                                  Reschedule
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this appointment?')) {
                                      onCancel(apt.id);
                                      setShowDayView(false);
                                    }
                                  }}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium whitespace-nowrap"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Reschedule Appointment</h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">User: <span className="font-semibold">{selectedAppointment.patient_name}</span></p>
              <p className="text-sm text-gray-600">Service: <span className="font-semibold">{selectedAppointment.service_name}</span></p>
              <p className="text-sm text-gray-600">Current: <span className="font-semibold">{selectedAppointment.appointment_date} at {selectedAppointment.appointment_time}</span></p>
            </div>

            {/* Add to Calendar Button */}
            <div className="mb-4">
              <AddToCalendar appointment={selectedAppointment} compact={false} />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Date
                </label>
                <input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Time
                </label>
                <select
                  value={rescheduleData.time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select time</option>
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleRescheduleSubmit}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Reschedule
              </button>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedAppointment(null);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel this appointment?')) {
                    onCancel(selectedAppointment.id);
                    setShowRescheduleModal(false);
                    setSelectedAppointment(null);
                  }
                }}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderCalendarView;
